const idr = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  maximumFractionDigits: 0,
})

const dateTime = new Intl.DateTimeFormat('id-ID', {
  dateStyle: 'medium',
  timeStyle: 'short',
})

const dateOnly = new Intl.DateTimeFormat('id-ID', {
  dateStyle: 'medium',
})

export function formatIDR(amount: number) {
  return idr.format(amount)
}

const compactUnits = [
  { limit: 1_000_000_000_000, suffix: 't' },
  { limit: 1_000_000_000, suffix: 'm' },
  { limit: 1_000_000, suffix: 'jt' },
] as const

/** `28_400_000` → `Rp 28,4 jt`. Three significant digits, as the dashboard shows. */
export function formatIDRCompact(amount: number) {
  const unit = compactUnits.find((candidate) => Math.abs(amount) >= candidate.limit)
  if (!unit) return formatIDR(amount)

  const value = amount / unit.limit
  const decimals = Math.abs(value) >= 100 ? 0 : Math.abs(value) >= 10 ? 1 : 2

  let text = value.toFixed(decimals)
  if (text.includes('.')) text = text.replace(/0+$/, '').replace(/\.$/, '')

  return `Rp ${text.replace('.', ',')} ${unit.suffix}`
}

export function formatDateTime(iso: string) {
  const parsed = new Date(iso)
  return Number.isNaN(parsed.getTime()) ? '-' : dateTime.format(parsed)
}

/** Date without the time, e.g. `28 Okt 2026`. */
export function formatDate(iso: string) {
  const parsed = new Date(iso)
  return Number.isNaN(parsed.getTime()) ? '-' : dateOnly.format(parsed)
}

const shortDate = new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'short' })

/**
 * Relative age for recent timestamps ("Baru saja", "5 menit lalu", "2 jam lalu",
 * "Kemarin"), falling back to a short date ("26 Okt") past a week. `now` is
 * injectable so the output is deterministic in tests.
 */
export function formatRelativeTime(iso: string, now: Date = new Date()) {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return '-'

  const diffSeconds = Math.round((now.getTime() - parsed.getTime()) / 1000)
  if (diffSeconds < 60) return 'Baru saja'

  const diffMinutes = Math.floor(diffSeconds / 60)
  if (diffMinutes < 60) return `${diffMinutes} menit lalu`

  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours} jam lalu`

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays === 1) return 'Kemarin'
  if (diffDays < 7) return `${diffDays} hari lalu`

  return shortDate.format(parsed)
}

export function formatWeight(grams: number) {
  return grams >= 1000 ? `${(grams / 1000).toFixed(1).replace('.', ',')} kg` : `${grams} g`
}

/** Digits only, without a leading 0 — +62 local numbers start at 8…, not 08…. */
export function sanitizeLocalPhoneDigits(value: string) {
  return value.replace(/\D/g, '').replace(/^0+/, '')
}

/** `+62` + `81234567890` → `+6281234567890`, stripping separators and a leading 0. */
export function toE164(dialCode: string, localNumber: string) {
  const digits = sanitizeLocalPhoneDigits(localNumber)
  return `${dialCode.startsWith('+') ? dialCode : `+${dialCode}`}${digits}`
}

/** Formats a phone number for display, e.g. `+6281234567890` → `+62 812-3456-7890` */
export function formatPhoneNumber(phone: string) {
  const cleaned = phone.trim()
  if (cleaned.startsWith('+62')) {
    const rest = cleaned.slice(3).replace(/\D/g, '')
    if (rest.length >= 7) {
      const p1 = rest.slice(0, 3)
      const p2 = rest.slice(3, 7)
      const p3 = rest.slice(7)
      return p3 ? `+62 ${p1}-${p2}-${p3}` : `+62 ${p1}-${p2}`
    }
  }
  return cleaned
}
